import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  Avatar,
  Button,
  TextField,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  useTheme,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import NotificationsIcon from '@mui/icons-material/Notifications';
import LockIcon from '@mui/icons-material/Lock';
import ChatIcon from '@mui/icons-material/Chat';
import ContactPhoneIcon from '@mui/icons-material/ContactPhone';
import { motion } from 'framer-motion';

const GuestUserDashboard = () => {
  const theme = useTheme();
  const currentUser = { name: 'Demo Guest' }; // Hardcoded for demo

  // State for clock
  const [currentTime, setCurrentTime] = useState('Loading...');

  // State for modals
  const [showPurchasedModal, setShowPurchasedModal] = useState(false);
  const [showPaywallModal, setShowPaywallModal] = useState(false);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [showPremiumAccessModal, setShowPremiumAccessModal] = useState(false);
  const [showChatDialog, setShowChatDialog] = useState(false);
  const [showContactDialog, setShowContactDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // State for ratings
  const [ratings, setRatings] = useState({
    priya: 0,
    arjun: 0,
  });

  // State for search filters
  const [searchForm, setSearchForm] = useState({
    age_min: '',
    age_max: '',
    location: '',
    qualification: '',
    gender: '',
    country: '',
    state: '',
    city: '',
    caste: '',
    raasi: '',
    nakshatra: '',
  });

  // Results and loading state
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [error, setError] = useState('');

  // Dropdown data (hardcoded demo data)
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [qualifications, setQualifications] = useState([]);
  const [raasis, setRaasis] = useState([]);
  const [nakshatras, setNakshatras] = useState([]);
  const [castes, setCastes] = useState([]);

  // Hardcoded maps for states and cities
  const stateMap = {
    'India': [{ name: 'Tamil Nadu' }, { name: 'Karnataka' }, { name: 'Andhra Pradesh' }],
    'USA': [{ name: 'California' }, { name: 'New York' }],
  };

  const cityMap = {
    'Tamil Nadu': [{ name: 'Chennai' }, { name: 'Coimbatore' }, { name: 'Madurai' }],
    'Karnataka': [{ name: 'Bangalore' }, { name: 'Mysore' }, { name: 'Hubli' }],
    'Andhra Pradesh': [{ name: 'Hyderabad' }, { name: 'Visakhapatnam' }],
    'California': [{ name: 'Los Angeles' }, { name: 'San Francisco' }],
    'New York': [{ name: 'New York City' }, { name: 'Buffalo' }],
  };

  // Hardcoded demo profiles for search
  const demoProfiles = [
    {
      id: 1,
      first_name: 'Priya',
      age: 28,
      gender: 'female',
      country: 'India',
      state: 'Tamil Nadu',
      city: 'Chennai',
      caste: 'Brahmin',
      raasi: 'Mesha',
      nakshatra: 'Ashwini',
      bio: 'Software Engineer from Chennai, looking for a compatible partner.',
      image: 'priya.jpg',
      email: 'priya@example.com',
      phone: '+91 98765 43210',
    },
    {
      id: 2,
      first_name: 'Arjun',
      age: 31,
      gender: 'male',
      country: 'India',
      state: 'Karnataka',
      city: 'Bangalore',
      caste: 'Kshatriya',
      raasi: 'Vrishabha',
      nakshatra: 'Bharani',
      bio: 'Doctor in Bangalore with interests in travel and music.',
      image: 'arjun.jpg',
      email: 'arjun@example.com',
      phone: '+91 87654 32109',
    },
    {
      id: 3,
      first_name: 'Anita',
      age: 26,
      gender: 'female',
      country: 'USA',
      state: 'California',
      city: 'Los Angeles',
      caste: 'Vaishya',
      raasi: 'Mithuna',
      nakshatra: 'Krittika',
      bio: 'Marketing professional in LA, enjoys hiking and reading.',
      image: null,
      email: 'anita@example.com',
      phone: '+1 555 123 4567',
    },
    {
      id: 4,
      first_name: 'Rahul',
      age: 29,
      gender: 'male',
      country: 'India',
      state: 'Andhra Pradesh',
      city: 'Hyderabad',
      caste: 'Brahmin',
      raasi: 'Simha',
      nakshatra: 'Rohini',
      bio: 'IT consultant from Hyderabad, passionate about cricket.',
      image: 'rahul.jpg',
      email: 'rahul@example.com',
      phone: '+91 76543 21098',
    },
    {
      id: 5,
      first_name: 'Sneha',
      age: 30,
      gender: 'female',
      country: 'USA',
      state: 'New York',
      city: 'New York City',
      caste: 'Kshatriya',
      raasi: 'Kanya',
      nakshatra: 'Mrigashira',
      bio: 'Finance analyst in NYC, loves art and cuisine.',
      image: 'sneha.jpg',
      email: 'sneha@example.com',
      phone: '+1 555 987 6543',
    },
  ];

  // Sample chat data
  const sampleChats = {
    Priya: [
      { sender: 'You', message: 'Hi Priya, how are you?', timestamp: 'Aug 20, 2025 10:30 AM' },
      { sender: 'Priya', message: 'Hello! I’m doing great, thanks for asking!', timestamp: 'Aug 20, 2025 10:32 AM' },
      { sender: 'You', message: 'Great to hear! What do you like to do in your free time?', timestamp: 'Aug 20, 2025 10:35 AM' },
      { sender: 'Priya', message: 'I enjoy coding and exploring new cafes in Chennai.', timestamp: 'Aug 20, 2025 10:40 AM' },
    ],
    Anita: [
      { sender: 'You', message: 'Hi Anita, loved reading about your hiking interest!', timestamp: 'Aug 21, 2025 2:15 PM' },
      { sender: 'Anita', message: 'Thanks! I hike every weekend in the LA hills.', timestamp: 'Aug 21, 2025 2:20 PM' },
    ],
    Sneha: [
      { sender: 'You', message: 'Hi Sneha, any favorite art galleries in NYC?', timestamp: 'Aug 22, 2025 9:00 AM' },
      { sender: 'Sneha', message: 'Hi! I love visiting the Met and MoMA.', timestamp: 'Aug 22, 2025 9:10 AM' },
      { sender: 'You', message: 'Awesome, I’ll check those out next time I’m in NYC!', timestamp: 'Aug 22, 2025 9:15 AM' },
    ],
  };

  // State for interest count
  const [interestCount, setInterestCount] = useState(0);
  const INTEREST_LIMIT = 3;

  // State for shortlisted profiles
  const [shortlistedProfiles, setShortlistedProfiles] = useState(new Set());

  // State for referral email
  const [referralEmail, setReferralEmail] = useState('');

  // State for purchased profiles (hardcoded demo data, aligned with demoProfiles)
  const [purchasedProfiles, setPurchasedProfiles] = useState([]);
  const [loadingPurchased, setLoadingPurchased] = useState(false);

  // Check if user is authenticated (bypassed for demo)
  useEffect(() => {
    console.log('Demo mode: Authenticated as guest');
  }, []);

  // Update clock
  useEffect(() => {
    const updateClock = () => {
      const now = new Date();
      setCurrentTime(now.toLocaleTimeString('en-US'));
    };
    updateClock();
    const intervalId = setInterval(updateClock, 1000);
    return () => clearInterval(intervalId);
  }, []);

  // Load dropdown data on component mount (hardcoded)
  useEffect(() => {
    setCountries([{ name: 'India' }, { name: 'USA' }]);
    setQualifications([{ name: 'B.Tech' }, { name: 'M.Tech' }, { name: 'MBA' }]);
    setRaasis([{ name: 'Mesha' }, { name: 'Vrishabha' }, { name: 'Mithuna' }, { name: 'Simha' }, { name: 'Kanya' }]);
    setNakshatras([{ name: 'Ashwini' }, { name: 'Bharani' }, { name: 'Krittika' }, { name: 'Rohini' }, { name: 'Mrigashira' }]);
    setCastes([{ name: 'Brahmin' }, { name: 'Kshatriya' }, { name: 'Vaishya' }]);
  }, []);

  // Load purchased profiles on component mount (hardcoded)
  useEffect(() => {
    setLoadingPurchased(true);
    // Simulate delay for loading
    setTimeout(() => {
      setPurchasedProfiles([
        {
          user: { ...demoProfiles[0], id: 1 }, // Priya
          amount: '100',
        },
        {
          user: { ...demoProfiles[2], id: 3 }, // Anita
          amount: '150',
        },
        {
          user: { ...demoProfiles[4], id: 5 }, // Sneha
          amount: '200',
        },
      ]);
      setLoadingPurchased(false);
    }, 1000);
  }, []);

  // Handle form changes
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setSearchForm((prev) => ({ ...prev, [name]: value }));
    if (name === 'country') {
      setSearchForm((prev) => ({ ...prev, state: '', city: '' }));
      setStates(stateMap[value] || []);
      setCities([]);
    }
    if (name === 'state') {
      setSearchForm((prev) => ({ ...prev, city: '' }));
      setCities(cityMap[value] || []);
    }
  };

  // Handle search (filter demo profiles)
  const handleSearch = () => {
    setLoading(true);
    setError('');
    setSearched(true);
    // Simulate API delay
    setTimeout(() => {
      const results = demoProfiles.filter((profile) => {
        if (searchForm.age_min && profile.age < parseInt(searchForm.age_min)) return false;
        if (searchForm.age_max && profile.age > parseInt(searchForm.age_max)) return false;
        if (searchForm.gender && profile.gender !== searchForm.gender) return false;
        if (searchForm.country && profile.country !== searchForm.country) return false;
        if (searchForm.state && profile.state !== searchForm.state) return false;
        if (searchForm.city && profile.city !== searchForm.city) return false;
        if (searchForm.caste && profile.caste !== searchForm.caste) return false;
        if (searchForm.raasi && profile.raasi !== searchForm.raasi) return false;
        if (searchForm.nakshatra && profile.nakshatra !== searchForm.nakshatra) return false;
        return true;
      });
      setSearchResults(results);
      setLoading(false);
    }, 1000);
  };

  // Handle interest button click
  const handleInterestClick = () => {
    if (interestCount < INTEREST_LIMIT) {
      setInterestCount((prev) => prev + 1);
    } else {
      setShowLimitModal(true);
    }
  };

  // Handle shortlist button click
  const handleShortlistClick = (profileId) => {
    setShortlistedProfiles((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(profileId)) {
        newSet.delete(profileId);
      } else {
        newSet.add(profileId);
      }
      return newSet;
    });
  };

  // Handle referral submission
  const handleReferralSubmit = () => {
    if (referralEmail) {
      alert(`Referral sent to ${referralEmail}!`);
      setReferralEmail('');
    }
  };

  // Handle star rating
  const handleRating = (name, value) => {
    setRatings((prev) => ({ ...prev, [name]: value }));
  };

  // Handle modal close
  const handleCloseModal = () => {
    setShowPaywallModal(false);
    setShowLimitModal(false);
    setShowPurchasedModal(false);
    setShowChatDialog(false);
    setShowContactDialog(false);
    setSelectedUser(null);
  };

  // Handle premium features
  const handlePremiumFeature = () => {
    setShowPaywallModal(true);
  };

  // Handle premium field access
  const handlePremiumFieldClick = (fieldName) => {
    setShowPremiumAccessModal(true);
  };

  // Handle chat button click
  const handleChatClick = (user) => {
    setSelectedUser(user);
    setShowChatDialog(true);
  };

  // Handle contact details button click
  const handleContactDetailsClick = (user) => {
    setSelectedUser(user);
    setShowContactDialog(true);
  };

  // Calculate total amount spent on profiles
  const calculateTotalAmount = () => {
    return purchasedProfiles.reduce((total, purchase) => {
      const amount = parseFloat(purchase.amount) || 0;
      return total + amount;
    }, 0).toFixed(2);
  };

  // Field styling for search form
  const fieldSx = {
    borderRadius: '12px',
    backgroundColor: 'rgba(255,255,255,0.95)',
    minWidth: 240,
    maxWidth: 340,
    fontSize: '0.95rem',
    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
    '& .MuiOutlinedInput-root': {
      '&:hover fieldset': {
        borderColor: '#4a1e28',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#4a1e28',
        borderWidth: '1px',
      },
    },
    '& .MuiInputLabel-root': {
      color: theme.palette.text.secondary,
      '&.Mui-focused': {
        color: '#4a1e28',
      },
    },
  };

  // Render select field helper
  const renderSelectField = (name, label, options, gridMd = 4) => (
    <Grid item xs={12} md={6} lg={gridMd}>
      <FormControl fullWidth sx={{ minWidth: 240, maxWidth: 340 }}>
        <InputLabel sx={{ color: theme.palette.text.secondary }}>
          {label}
        </InputLabel>
        <Select
          name={name}
          value={searchForm[name] || ''}
          onChange={handleFormChange}
          label={label}
          fullWidth
          sx={{
            ...fieldSx,
            py: 0.4,
          }}
          MenuProps={{
            PaperProps: {
              sx: {
                minWidth: 240,
                maxWidth: 340,
                fontSize: '0.95rem',
                borderRadius: '12px',
                mt: 1,
                boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
              },
            },
          }}
        >
          <MenuItem value="">
            <em>Select {label}</em>
          </MenuItem>
          {options.map((option) => (
            <MenuItem
              key={option.name}
              value={option.name}
              sx={{ minWidth: 240, maxWidth: 240, fontSize: '0.95rem' }}
            >
              {option.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Grid>
  );

  // Render premium locked field helper
  const renderPremiumLockedField = (name, label, gridMd = 4) => (
    <Grid item xs={12} md={6} lg={gridMd}>
      <FormControl fullWidth sx={{ minWidth: 240, maxWidth: 340 }}>
        <InputLabel sx={{ color: '#6b7280' }}>
          {label} 🔒
        </InputLabel>
        <Select
          name={name}
          value=""
          disabled
          label={`${label} 🔒`}
          fullWidth
          onClick={() => handlePremiumFieldClick(name)}
          sx={{
            ...fieldSx,
            py: 0.4,
            bgcolor: '#f3f4f6',
            cursor: 'pointer',
            '&:hover': {
              bgcolor: '#e5e7eb',
            },
            '& .MuiSelect-icon': {
              color: '#6b7280',
            },
          }}
        >
          <MenuItem value="" disabled>
            <em>Premium Feature - Click to Unlock</em>
          </MenuItem>
        </Select>
      </FormControl>
    </Grid>
  );

  // Render star rating component
  const StarRating = ({ name, currentRating }) => {
    return (
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        {[1, 2, 3, 4, 5].map((value) => (
          <IconButton
            key={value}
            size="small"
            onClick={() => handleRating(name, value)}
            sx={{
              color: value <= currentRating ? '#facc15' : '#d1d5db',
              '&:hover': { color: '#facc15', transform: 'scale(1.2)' },
              p: 0.5,
              transition: 'all 0.2s ease-in-out',
            }}
            component={motion.button}
            whileHover={{ scale: 1.2 }}
            whileTap={{ scale: 0.9 }}
          >
            <StarIcon />
          </IconButton>
        ))}
      </Box>
    );
  };

  // For demo, always show dashboard (bypass auth check)
  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#f9fafb',
        pb: 24,
      }}
    >
      <Container maxWidth="lg" sx={{ py: 6 }}>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pb: 4,
            borderBottom: '1px solid #e5e7eb',
            mb: 8,
          }}
          component={motion.div}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Typography
            variant="h3"
            sx={{
              fontWeight: 800,
              color: '#4a1e28',
              fontSize: { xs: '1.8rem', md: '2.5rem' },
              letterSpacing: '-0.025em',
            }}
          >
            Chiranjeevi Dashboard
          </Typography>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: '#374151' }}>
              Welcome, {currentUser?.name || 'Guest User'}!
            </Typography>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: '#0d9488',
                fontFamily: 'monospace',
                letterSpacing: '0.05em',
              }}
            >
              {currentTime}
            </Typography>
            {/* Debug info */}
            <Typography variant="caption" sx={{ color: '#6b7280' }}>
              Auth: Demo | Token: Yes
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151' }}>
                Your Rating
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: '#facc15',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                4.5 <StarIcon sx={{ fontSize: '1.2rem' }} />
              </Typography>
            </Box>
            <Box sx={{ position: 'relative' }}>
              <IconButton
                sx={{
                  color: '#4a1e28',
                  fontSize: '2rem',
                  cursor: 'not-allowed',
                  '&:hover': { bgcolor: '#f3f4f6' },
                }}
                disabled
              >
                <NotificationsIcon fontSize="large" />
              </IconButton>
              <Box
                sx={{
                  position: 'absolute',
                  top: -8,
                  right: -8,
                  bgcolor: '#4a1e28',
                  color: 'white',
                  borderRadius: '50%',
                  width: 20,
                  height: 20,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.75rem',
                }}
              >
                <LockIcon sx={{ fontSize: '0.75rem' }} />
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Main Content */}
        <Box sx={{ mb: 8 }}>
          {/* Activity & KPIs Section */}
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: '#4a1e28',
              mb: 4,
              fontSize: '1.5rem',
              letterSpacing: '-0.025em',
            }}
          >
            Your Activity & KPIs
          </Typography>
          <Grid container spacing={4} sx={{ mb: 8 }}>
            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  p: 6,
                  borderRadius: 3,
                  background: 'white',
                  boxShadow: '0 6px 20px rgba(0,0,0,0.1)',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  transition: 'transform 0.3s ease-in-out',
                  '&:hover': { transform: 'translateY(-8px)' },
                }}
                component={motion.div}
                whileHover={{ scale: 1.02 }}
              >
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#6b7280', mb: 2 }}>
                    Profiles Purchased
                  </Typography>
                  <Typography
                    variant="h2"
                    sx={{
                      fontWeight: 800,
                      color: '#4a1e28',
                      mb: 2,
                      fontSize: { xs: '2.5rem', md: '3rem' },
                    }}
                  >
                    {purchasedProfiles.length}
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#6b7280' }}>
                    Total Spent: ₹{calculateTotalAmount()}
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  onClick={() => setShowPurchasedModal(true)}
                  sx={{
                    mt: 4,
                    bgcolor: '#4a1e28',
                    color: 'white',
                    fontWeight: 600,
                    py: 2,
                    px: 4,
                    borderRadius: 2,
                    '&:hover': {
                      bgcolor: '#3b1720',
                    },
                    transition: 'all 0.3s ease',
                  }}
                  fullWidth
                >
                  View Details
                </Button>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  p: 6,
                  borderRadius: 3,
                  background: 'white',
                  boxShadow: '0 6px 20px rgba(0,0,0,0.1)',
                  height: '100%',
                  transition: 'transform 0.3s ease-in-out',
                  '&:hover': { transform: 'translateY(-8px)' },
                }}
                component={motion.div}
                whileHover={{ scale: 1.02 }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#6b7280', mb: 2 }}>
                  Refunds & Disputes
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontWeight: 800,
                    color: '#4a1e28',
                    mb: 2,
                    fontSize: { xs: '2.5rem', md: '3rem' },
                  }}
                >
                  2
                </Typography>
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" sx={{ color: '#6b7280', mb: 1, fontSize: '0.75rem' }}>
                    • Refunds for no response (&gt;3 days) are reviewed case-by-case.
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#6b7280', mb: 1, fontSize: '0.75rem' }}>
                    • Issues or arguments result in credits.
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#6b7280', fontSize: '0.75rem' }}>
                    • Accounts may be paused after 2-3 disputes.
                  </Typography>
                </Box>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  p: 6,
                  borderRadius: 3,
                  background: 'white',
                  boxShadow: '0 6px 20px rgba(0,0,0,0.1)',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  transition: 'transform 0.3s ease-in-out',
                  '&:hover': { transform: 'translateY(-8px)' },
                }}
                component={motion.div}
                whileHover={{ scale: 1.02 }}
              >
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#6b7280', mb: 2 }}>
                    My Wallet
                  </Typography>
                  <Typography
                    variant="h2"
                    sx={{
                      fontWeight: 800,
                      color: '#4a1e28',
                      mb: 2,
                      fontSize: { xs: '2.5rem', md: '3rem' },
                    }}
                  >
                    ₹400
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#6b7280' }}>
                    Available Credits
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  sx={{
                    mt: 4,
                    bgcolor: '#facc15',
                    color: '#1f2937',
                    fontWeight: 600,
                    py: 2,
                    px: 4,
                    borderRadius: 2,
                    '&:hover': {
                      bgcolor: '#eab308',
                    },
                    transition: 'all 0.3s ease',
                  }}
                  fullWidth
                >
                  Credits & Promos
                </Button>
              </Card>
            </Grid>
          </Grid>

          {/* Main Grid Layout */}
          <Grid container spacing={4}>
            <Grid item xs={12} lg={8}>
              {/* Find Your Match Section */}
              <Card
                sx={{
                  p: 6,
                  borderRadius: 3,
                  background: 'white',
                  boxShadow: '0 6px 20px rgba(0,0,0,0.1)',
                  transition: 'transform 0.3s ease-in-out',
                  '&:hover': { transform: 'translateY(-8px)' },
                  mb: 4,
                }}
                component={motion.div}
                whileHover={{ scale: 1.02 }}
              >
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    color: '#4a1e28',
                    mb: 4,
                    fontSize: '1.5rem',
                    letterSpacing: '-0.025em',
                  }}
                >
                  Find Your Match
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6} lg={4}>
                    <TextField
                      fullWidth
                      label="Min Age"
                      name="age_min"
                      type="number"
                      value={searchForm.age_min}
                      onChange={handleFormChange}
                      variant="outlined"
                      sx={fieldSx}
                    />
                  </Grid>
                  <Grid item xs={12} md={6} lg={4}>
                    <TextField
                      fullWidth
                      label="Max Age"
                      name="age_max"
                      type="number"
                      value={searchForm.age_max}
                      onChange={handleFormChange}
                      variant="outlined"
                      sx={fieldSx}
                    />
                  </Grid>
                  {renderSelectField(
                    'gender',
                    'Gender',
                    [
                      { name: 'Male' },
                      { name: 'Female' },
                    ],
                    4
                  )}
                  {renderSelectField('country', 'Country', countries, 4)}
                  {renderSelectField('state', 'State', states, 4)}
                  {renderSelectField('city', 'City', cities, 4)}
                  {renderSelectField('caste', 'Caste', castes, 4)}
                  {renderSelectField('raasi', 'Raasi', raasis, 4)}
                  {renderSelectField('nakshatra', 'Nakshatra', nakshatras, 4)}
                  {renderPremiumLockedField('qualification', 'Education', 4)}
                  {renderPremiumLockedField('income', 'Income', 4)}
                </Grid>
                <Button
                  variant="contained"
                  onClick={handleSearch}
                  disabled={loading}
                  sx={{
                    mt: 3,
                    bgcolor: '#4a1e28',
                    color: 'white',
                    fontWeight: 700,
                    py: 1.5,
                    px: 3,
                    borderRadius: 2,
                    '&:hover': {
                      bgcolor: '#3b1720',
                    },
                    transition: 'all 0.3s ease',
                  }}
                  fullWidth
                >
                  {loading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    'Search Profiles'
                  )}
                </Button>
              </Card>

              {/* Error Alert */}
              {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {error}
                </Alert>
              )}

              {/* Search Results Section */}
              {searched && (
                <Box>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                      color: '#4a1e28',
                      mb: 4,
                      fontSize: '1.5rem',
                      letterSpacing: '-0.025em',
                    }}
                  >
                    Search Results
                  </Typography>
                  <Grid container spacing={3}>
                    {searchResults.length > 0 ? (
                      searchResults.map((profile, index) => (
                        <Grid item xs={12} md={6} key={profile.id || index}>
                          <Card
                            sx={{
                              borderRadius: 3,
                              boxShadow: '0 6px 20px rgba(0,0,0,0.1)',
                              overflow: 'hidden',
                              transition: 'transform 0.3s ease-in-out',
                              '&:hover': { transform: 'translateY(-8px)' },
                            }}
                            component={motion.div}
                            whileHover={{ scale: 1.02 }}
                          >
                            <Box sx={{ position: 'relative' }}>
                              {profile.image ? (
                                <img
                                  src={profile.image}
                                  alt={`${profile.first_name || profile.name || 'Unknown'}'s Profile Picture`}
                                  style={{
                                    width: '100%',
                                    height: '192px',
                                    objectFit: 'cover',
                                  }}
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.nextSibling.style.display = 'flex';
                                  }}
                                />
                              ) : null}
                              <Box
                                sx={{
                                  display: profile.image ? 'none' : 'flex',
                                  width: '100%',
                                  height: '192px',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  bgcolor: index % 2 === 0 ? '#4a1e28' : '#0d9488',
                                  color: 'white',
                                  fontSize: '4rem',
                                  fontWeight: 700,
                                  textTransform: 'uppercase',
                                }}
                              >
                                {(profile.first_name || profile.name || 'U').charAt(0)}
                              </Box>
                            </Box>
                            <Box sx={{ p: 2 }}>
                              <Typography variant="h5" sx={{ fontWeight: 700, color: '#1f2937' }}>
                                {profile.first_name || profile.name || 'Unknown'}, {profile.age || 'N/A'}
                              </Typography>
                              <Box sx={{ position: 'relative', mt: 2 }}>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: '#6b7280',
                                    filter: 'blur(4px)',
                                    userSelect: 'none',
                                  }}
                                >
                                  {profile.bio || profile.description || 'Profile description is blurred for guest users. Upgrade to view full details...'}
                                </Typography>
                                <Box
                                  sx={{
                                    position: 'absolute',
                                    inset: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    bgcolor: 'rgba(255,255,255,0.7)',
                                  }}
                                >
                                  <Button
                                    variant="contained"
                                    onClick={handlePremiumFeature}
                                    sx={{
                                      bgcolor: '#facc15',
                                      color: '#1f2937',
                                      fontWeight: 700,
                                      py: 1,
                                      px: 2.5,
                                      borderRadius: 3,
                                      '&:hover': {
                                        bgcolor: '#eab308',
                                      },
                                      transition: 'all 0.3s ease',
                                    }}
                                    component={motion.button}
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                  >
                                    View Full Profile 🔒
                                  </Button>
                                </Box>
                              </Box>
                            </Box>
                            <Box sx={{ p: 2, borderTop: '1px solid #e5e7eb' }}>
                              <Grid container spacing={1}>
                                <Grid item xs={4}>
                                  <Button
                                    variant={shortlistedProfiles.has(profile.id || index) ? 'contained' : 'outlined'}
                                    onClick={() => handleShortlistClick(profile.id || index)}
                                    sx={{
                                      borderColor: shortlistedProfiles.has(profile.id || index) ? '#4a1e28' : '#e5e7eb',
                                      bgcolor: shortlistedProfiles.has(profile.id || index) ? '#4a1e28' : 'transparent',
                                      color: shortlistedProfiles.has(profile.id || index) ? 'white' : '#1f2937',
                                      borderRadius: 3,
                                      py: 1,
                                      px: 1.5,
                                      fontSize: '0.875rem',
                                      '&:hover': {
                                        borderColor: shortlistedProfiles.has(profile.id || index) ? '#3b1720' : '#4a1e28',
                                        bgcolor: shortlistedProfiles.has(profile.id || index) ? '#3b1720' : 'transparent',
                                      },
                                      transition: 'all 0.3s ease',
                                    }}
                                    fullWidth
                                    component={motion.button}
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                  >
                                    ❤️ Shortlist
                                  </Button>
                                </Grid>
                                <Grid item xs={4}>
                                  <Button
                                    variant="outlined"
                                    onClick={handleInterestClick}
                                    sx={{
                                      borderColor: '#e5e7eb',
                                      color: '#1f2937',
                                      borderRadius: 3,
                                      py: 1,
                                      px: 1.5,
                                      fontSize: '0.875rem',
                                      '&:hover': {
                                        borderColor: '#4a1e28',
                                      },
                                      transition: 'all 0.3s ease',
                                    }}
                                    fullWidth
                                    component={motion.button}
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                  >
                                    👋 Interest ({interestCount}/{INTEREST_LIMIT})
                                  </Button>
                                </Grid>
                                <Grid item xs={4}>
                                  <Button
                                    variant="outlined"
                                    onClick={handlePremiumFeature}
                                    disabled
                                    sx={{
                                      bgcolor: '#f3f4f6',
                                      color: '#6b7280',
                                      borderRadius: 3,
                                      py: 1,
                                      px: 1.5,
                                      fontSize: '0.875rem',
                                      borderColor: '#e5e7eb',
                                    }}
                                    fullWidth
                                  >
                                    💬 Chat 🔒
                                  </Button>
                                </Grid>
                              </Grid>
                            </Box>
                          </Card>
                        </Grid>
                      ))
                    ) : (
                      <Grid item xs={12}>
                        <Card sx={{ p: 4, textAlign: 'center', bgcolor: 'white', boxShadow: '0 6px 20px rgba(0,0,0,0.1)' }}>
                          <Typography variant="h6" color="textSecondary">
                            No profiles found matching your criteria. Try adjusting your search filters.
                          </Typography>
                        </Card>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              )}
            </Grid>

            {/* Sidebar */}
            <Grid item xs={12} lg={4}>
              <Grid container spacing={3}>
                {/* Rate Your Interactions */}
                <Grid item xs={12} md={4}>
                  <Card
                    sx={{
                      p: 3,
                      borderRadius: 3,
                      background: 'white',
                      boxShadow: '0 6px 20px rgba(0,0,0,0.1)',
                      transition: 'transform 0.3s ease-in-out',
                      '&:hover': { transform: 'translateY(-8px)' },
                      height: '100%',
                    }}
                    component={motion.div}
                    whileHover={{ scale: 1.02 }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        color: '#4a1e28',
                        mb: 2,
                        fontSize: '1.1rem',
                        letterSpacing: '-0.025em',
                      }}
                    >
                      Rate Your Interactions
                    </Typography>
                    <Box sx={{ space: 2 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          p: 1.5,
                          bgcolor: '#f9fafb',
                          borderRadius: 2,
                          mb: 2,
                          transition: 'background 0.3s ease',
                          '&:hover': { bgcolor: '#f1f5f9' },
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Avatar
                            sx={{ 
                              width: 40, 
                              height: 40, 
                              transition: 'transform 0.3s ease', 
                              '&:hover': { transform: 'scale(1.1)' },
                              bgcolor: '#4a1e28'
                            }}
                          >
                            P
                          </Avatar>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#1f2937' }}>
                              Priya, 28
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#6b7280' }}>
                              Aug 15, 2025
                            </Typography>
                          </Box>
                        </Box>
                        <StarRating name="priya" currentRating={ratings.priya} />
                      </Box>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          p: 1.5,
                          bgcolor: '#f9fafb',
                          borderRadius: 2,
                          transition: 'background 0.3s ease',
                          '&:hover': { bgcolor: '#f1f5f9' },
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Avatar
                            sx={{ 
                              width: 40, 
                              height: 40, 
                              transition: 'transform 0.3s ease', 
                              '&:hover': { transform: 'scale(1.1)' },
                              bgcolor: '#0d9488'
                            }}
                          >
                            A
                          </Avatar>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#1f2937' }}>
                              Arjun, 31
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#6b7280' }}>
                              Aug 12, 2025
                            </Typography>
                          </Box>
                        </Box>
                        <StarRating name="arjun" currentRating={ratings.arjun} />
                      </Box>
                    </Box>
                  </Card>
                </Grid>

                {/* Your Guest Access */}
                <Grid item xs={12} md={4}>
                  <Card
                    sx={{
                      p: 3,
                      borderRadius: 3,
                      background: 'white',
                      boxShadow: '0 6px 20px rgba(0,0,0,0.1)',
                      transition: 'transform 0.3s ease-in-out',
                      '&:hover': { transform: 'translateY(-8px)' },
                      height: '100%',
                    }}
                    component={motion.div}
                    whileHover={{ scale: 1.02 }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        color: '#4a1e28',
                        mb: 2,
                        fontSize: '1.1rem',
                        letterSpacing: '-0.025em',
                      }}
                    >
                      Your Guest Access
                    </Typography>
                    <Box component="ul" sx={{ space: 1, listStyle: 'none', p: 0, m: 0 }}>
                      <Box component="li" sx={{ display: 'flex', alignItems: 'center', mb: 1, fontSize: '0.875rem' }}>
                        <span style={{ marginRight: '0.5rem', fontSize: '1rem' }}>✅</span>
                        Search with 9 filters
                      </Box>
                      <Box component="li" sx={{ display: 'flex', alignItems: 'center', mb: 1, fontSize: '0.875rem' }}>
                        <span style={{ marginRight: '0.5rem', fontSize: '1rem' }}>✅</span>
                        Shortlist profiles
                      </Box>
                      <Box component="li" sx={{ display: 'flex', alignItems: 'center', mb: 1, fontSize: '0.875rem' }}>
                        <span style={{ marginRight: '0.5rem', fontSize: '1rem' }}>✅</span>
                        Express Interest (3 per day)
                      </Box>
                      <Box component="li" sx={{ display: 'flex', alignItems: 'center', mb: 1, fontSize: '0.875rem' }}>
                        <span style={{ marginRight: '0.5rem', fontSize: '1rem' }}>✅</span>
                        Chat / Contact details
                      </Box>
                      <Box component="li" sx={{ display: 'flex', alignItems: 'center', fontSize: '0.875rem', color: '#6b7280' }}>
                        <span style={{ marginRight: '0.5rem', fontSize: '1rem' }}>🔒</span>
                        View Full Profiles
                      </Box>
                    </Box>
                  </Card>
                </Grid>

                {/* Refer a Friend */}
                <Grid item xs={12} md={4}>
                  <Card
                    sx={{
                      p: 3,
                      borderRadius: 3,
                      background: 'white',
                      boxShadow: '0 6px 20px rgba(0,0,0,0.1)',
                      transition: 'transform 0.3s ease-in-out',
                      '&:hover': { transform: 'translateY(-8px)' },
                      height: '100%',
                    }}
                    component={motion.div}
                    whileHover={{ scale: 1.02 }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        color: '#4a1e28',
                        mb: 2,
                        fontSize: '1.1rem',
                        letterSpacing: '-0.025em',
                      }}
                    >
                      Refer a Friend
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#6b7280', mb: 3, lineHeight: 1.4, fontSize: '0.875rem' }}>
                      You both get a ₹200 coupon when they make their first purchase!
                    </Typography>
                    <TextField
                      fullWidth
                      type="email"
                      placeholder="Enter friend's email"
                      value={referralEmail}
                      onChange={(e) => setReferralEmail(e.target.value)}
                      size="small"
                      sx={{
                        mb: 2,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&:hover fieldset': { borderColor: '#4a1e28' },
                          '&.Mui-focused fieldset': { borderColor: '#4a1e28' },
                        },
                      }}
                    />
                    <Button
                      variant="contained"
                      onClick={handleReferralSubmit}
                      sx={{
                        bgcolor: '#facc15',
                        color: '#1f2937',
                        fontWeight: 600,
                        py: 1.5,
                        px: 2,
                        borderRadius: 2,
                        fontSize: '0.875rem',
                        '&:hover': {
                          bgcolor: '#eab308',
                        },
                        transition: 'all 0.3s ease',
                      }}
                      fullWidth
                      component={motion.button}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      Send Referral
                    </Button>
                  </Card>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>

        {/* Footer */}
        <Box
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            bgcolor: '#4a1e28',
            color: 'white',
            p: 4,
            zIndex: 10,
            boxShadow: '0 -6px 20px rgba(0,0,0,0.15)',
          }}
        >
          <Container maxWidth="lg">
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexDirection: { xs: 'column', md: 'row' },
                gap: 2,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  textAlign: { xs: 'center', md: 'left' },
                  letterSpacing: '0.025em',
                }}
              >
                Upgrade to unlock full features and find your match faster!
              </Typography>
              <Button
                variant="contained"
                sx={{
                  bgcolor: '#facc15',
                  color: '#1f2937',
                  fontWeight: 600,
                  py: 1.5,
                  px: 8,
                  borderRadius: 3,
                  '&:hover': {
                    bgcolor: '#eab308',
                  },
                  transition: 'all 0.3s ease',
                }}
                component={motion.button}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Upgrade to Premium
              </Button>
            </Box>
          </Container>
        </Box>

        {/* Purchased Profiles Modal */}
        {showPurchasedModal && (
          <Box
            sx={{
              position: 'fixed',
              inset: 0,
              bgcolor: 'rgba(0,0,0,0.75)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 50,
            }}
            onClick={handleCloseModal}
            component={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <Card
              sx={{
                maxWidth: 'md',
                width: '90%',
                maxHeight: '80vh',
                overflow: 'auto',
                p: 6,
                borderRadius: 3,
                background: 'white',
                boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
              }}
              onClick={(e) => e.stopPropagation()}
              component={motion.div}
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 700,
                  color: '#4a1e28',
                  mb: 4,
                  textAlign: 'center',
                  fontSize: { xs: '1.8rem', md: '2.5rem' },
                  letterSpacing: '-0.025em',
                }}
              >
                Your Purchased Profiles ({purchasedProfiles.length})
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', alignItems: 'center' }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    setLoadingPurchased(true);
                    setTimeout(() => setLoadingPurchased(false), 500); // Simulate refresh
                  }}
                  sx={{
                    borderColor: '#4a1e28',
                    color: '#4a1e28',
                    '&:hover': { borderColor: '#3b1720' },
                  }}
                >
                  Refresh
                </Button>
              </Box>
              {loadingPurchased ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size={32} />
                </Box>
              ) : purchasedProfiles.length > 0 ? (
                <Grid container spacing={3} sx={{ mb: 4 }}>
                  {purchasedProfiles.map((purchase, index) => (
                    <Grid item xs={12} key={index}>
                      <Card
                        sx={{
                          borderRadius: 3,
                          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                          p: 3,
                          bgcolor: '#f9fafb',
                        }}
                        component={motion.div}
                        whileHover={{ scale: 1.01 }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Avatar
                            src={purchase.user?.image}
                            sx={{
                              width: 60,
                              height: 60,
                              mr: 2,
                              transition: 'transform 0.3s ease',
                              '&:hover': { transform: 'scale(1.1)' },
                              bgcolor: purchase.user?.image ? 'transparent' : '#4a1e28',
                            }}
                            alt={purchase.user?.first_name || purchase.user?.name || 'User Avatar'}
                          >
                            {!purchase.user?.image && (purchase.user?.first_name?.charAt(0) || purchase.user?.name?.charAt(0) || 'U')}
                          </Avatar>
                          <Box>
                            <Typography variant="h6" sx={{ fontWeight: 600, color: '#1f2937' }}>
                              {purchase.user?.first_name || purchase.user?.name || 'Unknown User'}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#0d9488', fontWeight: 600 }}>
                              Purchased for ₹{purchase.amount}
                            </Typography>
                          </Box>
                        </Box>
                        <Box sx={{ pl: 2, mb: 2 }}>
                          <Typography variant="body2" sx={{ color: '#374151', mb: 1 }}>
                            <strong>Age:</strong> {purchase.user?.age || 'N/A'}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#374151', mb: 1 }}>
                            <strong>Gender:</strong> {purchase.user?.gender ? purchase.user.gender.charAt(0).toUpperCase() + purchase.user.gender.slice(1) : 'N/A'}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#374151', mb: 1 }}>
                            <strong>Location:</strong> {purchase.user?.city || 'N/A'}, {purchase.user?.state || 'N/A'}, {purchase.user?.country || 'N/A'}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#374151', mb: 1 }}>
                            <strong>Caste:</strong> {purchase.user?.caste || 'N/A'}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#374151', mb: 1 }}>
                            <strong>Raasi:</strong> {purchase.user?.raasi || 'N/A'}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#374151', mb: 1 }}>
                            <strong>Nakshatra:</strong> {purchase.user?.nakshatra || 'N/A'}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#374151' }}>
                            <strong>Bio:</strong> {purchase.user?.bio || 'No bio available'}
                          </Typography>
                        </Box>
                        <Box sx={{ pl: 2 }}>
                          <Grid container spacing={1}>
                            <Grid item xs={6}>
                              <Button
                                variant="contained"
                                onClick={() => handleChatClick(purchase.user)}
                                sx={{
                                  bgcolor: '#4a1e28',
                                  color: 'white',
                                  borderRadius: 3,
                                  py: 1,
                                  px: 1.5,
                                  fontSize: '0.875rem',
                                  '&:hover': {
                                    bgcolor: '#3b1720',
                                  },
                                  transition: 'all 0.3s ease',
                                }}
                                fullWidth
                                startIcon={<ChatIcon />}
                                component={motion.button}
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                              >
                                Chat
                              </Button>
                            </Grid>
                            <Grid item xs={6}>
                              <Button
                                variant="contained"
                                onClick={() => handleContactDetailsClick(purchase.user)}
                                sx={{
                                  bgcolor: '#4a1e28',
                                  color: 'white',
                                  borderRadius: 3,
                                  py: 1,
                                  px: 1.5,
                                  fontSize: '0.875rem',
                                  '&:hover': {
                                    bgcolor: '#3b1720',
                                  },
                                  transition: 'all 0.3s ease',
                                }}
                                fullWidth
                                startIcon={<ContactPhoneIcon />}
                                component={motion.button}
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                              >
                                Contact Details
                              </Button>
                            </Grid>
                          </Grid>
                        </Box>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="h6" sx={{ color: '#6b7280', mb: 2 }}>
                    No Profiles Purchased Yet
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#9ca3af' }}>
                    Start exploring profiles to make your first purchase!
                  </Typography>
                </Box>
              )}
              <Box sx={{ textAlign: 'center' }}>
                <Button
                  variant="outlined"
                  onClick={handleCloseModal}
                  sx={{
                    borderColor: '#4a1e28',
                    color: '#4a1e28',
                    fontWeight: 600,
                    py: 1.5,
                    px: 4,
                    borderRadius: 2,
                    '&:hover': {
                      borderColor: '#3b1720',
                      bgcolor: '#4a1e28',
                      color: 'white',
                    },
                    transition: 'all 0.3s ease',
                  }}
                  component={motion.button}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Close
                </Button>
              </Box>
            </Card>
          </Box>
        )}

        {/* Chat Dialog */}
        {showChatDialog && selectedUser && (
          <Dialog
            open={showChatDialog}
            onClose={handleCloseModal}
            maxWidth="sm"
            fullWidth
            component={motion.div}
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.3 }}
          >
            <DialogTitle sx={{ bgcolor: '#4a1e28', color: 'white', fontWeight: 600 }}>
              Chat with {selectedUser?.first_name || selectedUser?.name || 'Unknown User'}
            </DialogTitle>
            <DialogContent sx={{ p: 3, bgcolor: '#f9fafb' }}>
              {sampleChats[selectedUser?.first_name]?.length > 0 ? (
                sampleChats[selectedUser.first_name].map((chat, index) => (
                  <Box
                    key={index}
                    sx={{
                      mb: 2,
                      p: 2,
                      borderRadius: 2,
                      bgcolor: chat.sender === 'You' ? '#e5e7eb' : '#0d9488',
                      color: chat.sender === 'You' ? '#1f2937' : 'white',
                      maxWidth: '80%',
                      ml: chat.sender === 'You' ? 'auto' : 0,
                      mr: chat.sender === 'You' ? 2 : 'auto',
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {chat.message}
                    </Typography>
                    <Typography variant="caption" sx={{ color: chat.sender === 'You' ? '#6b7280' : '#e5e7eb', mt: 0.5 }}>
                      {chat.timestamp}
                    </Typography>
                  </Box>
                ))
              ) : (
                <Typography variant="body2" sx={{ color: '#6b7280', textAlign: 'center' }}>
                  No chat history available.
                </Typography>
              )}
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button
                variant="outlined"
                onClick={handleCloseModal}
                sx={{
                  borderColor: '#4a1e28',
                  color: '#4a1e28',
                  '&:hover': { borderColor: '#3b1720', bgcolor: '#4a1e28', color: 'white' },
                }}
              >
                Close
              </Button>
            </DialogActions>
          </Dialog>
        )}

        {/* Contact Details Dialog */}
        {showContactDialog && selectedUser && (
          <Dialog
            open={showContactDialog}
            onClose={handleCloseModal}
            maxWidth="sm"
            fullWidth
            component={motion.div}
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.3 }}
          >
            <DialogTitle sx={{ bgcolor: '#4a1e28', color: 'white', fontWeight: 600 }}>
              Contact Details for {selectedUser?.first_name || selectedUser?.name || 'Unknown User'}
            </DialogTitle>
            <DialogContent sx={{ p: 3, bgcolor: '#f9fafb' }}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body1" sx={{ color: '#1f2937', fontWeight: 600 }}>
                  Email:
                </Typography>
                <Typography variant="body2" sx={{ color: '#374151' }}>
                  {selectedUser?.email || 'Not available'}
                </Typography>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body1" sx={{ color: '#1f2937', fontWeight: 600 }}>
                  Phone:
                </Typography>
                <Typography variant="body2" sx={{ color: '#374151' }}>
                  {selectedUser?.phone || 'Not available'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body1" sx={{ color: '#1f2937', fontWeight: 600 }}>
                  Location:
                </Typography>
                <Typography variant="body2" sx={{ color: '#374151' }}>
                  {selectedUser?.city || 'N/A'}, {selectedUser?.state || 'N/A'}, {selectedUser?.country || 'N/A'}
                </Typography>
              </Box>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button
                variant="outlined"
                onClick={handleCloseModal}
                sx={{
                  borderColor: '#4a1e28',
                  color: '#4a1e28',
                  '&:hover': { borderColor: '#3b1720', bgcolor: '#4a1e28', color: 'white' },
                }}
              >
                Close
              </Button>
            </DialogActions>
          </Dialog>
        )}

        {/* Paywall Modal */}
        {showPaywallModal && (
          <Box
            sx={{
              position: 'fixed',
              inset: 0,
              bgcolor: 'rgba(0,0,0,0.75)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 50,
            }}
            onClick={handleCloseModal}
            component={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <Card
              sx={{
                maxWidth: 'md',
                width: '90%',
                p: 6,
                borderRadius: 3,
                textAlign: 'center',
                background: 'white',
                boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
              }}
              onClick={(e) => e.stopPropagation()}
              component={motion.div}
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 700,
                  color: '#4a1e28',
                  mb: 2,
                  fontSize: { xs: '1.8rem', md: '2.5rem' },
                  letterSpacing: '-0.025em',
                }}
              >
                Unlock Premium Access
              </Typography>
              <Typography variant="body1" sx={{ color: '#6b7280', mb: 4, lineHeight: 1.6 }}>
                Upgrade your account to view full profiles and access advanced features.
              </Typography>
              <Button
                variant="contained"
                sx={{
                  bgcolor: '#facc15',
                  color: '#1f2937',
                  fontWeight: 700,
                  py: 1.5,
                  px: 3,
                  borderRadius: 2,
                  fontSize: '1.125rem',
                  '&:hover': {
                    bgcolor: '#eab308',
                  },
                  transition: 'all 0.3s ease',
                }}
                fullWidth
                component={motion.button}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Upgrade Now
              </Button>
              <Button
                variant="text"
                onClick={handleCloseModal}
                sx={{
                  mt: 1.5,
                  color: '#6b7280',
                  '&:hover': {
                    bgcolor: 'transparent',
                    color: '#4a1e28',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                Maybe later
              </Button>
            </Card>
          </Box>
        )}

        {/* Limit Modal */}
        {showLimitModal && (
          <Box
            sx={{
              position: 'fixed',
              inset: 0,
              bgcolor: 'rgba(0,0,0,0.75)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 50,
            }}
            onClick={handleCloseModal}
            component={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <Card
              sx={{
                maxWidth: 'md',
                width: '90%',
                p: 6,
                borderRadius: 3,
                textAlign: 'center',
                background: 'white',
                boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
              }}
              onClick={(e) => e.stopPropagation()}
              component={motion.div}
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 700,
                  color: '#4a1e28',
                  mb: 2,
                  fontSize: { xs: '1.8rem', md: '2.5rem' },
                  letterSpacing: '-0.025em',
                }}
              >
                Daily Limit Reached
              </Typography>
              <Typography variant="body1" sx={{ color: '#6b7280', mb: 4, lineHeight: 1.6 }}>
                You've reached today's interest limit of 3. Upgrade for unlimited interests!
              </Typography>
              <Button
                variant="contained"
                sx={{
                  bgcolor: '#facc15',
                  color: '#1f2937',
                  fontWeight: 700,
                  py: 1.5,
                  px: 3,
                  borderRadius: 2,
                  fontSize: '1.125rem',
                  '&:hover': {
                    bgcolor: '#eab308',
                  },
                  transition: 'all 0.3s ease',
                }}
                fullWidth
                component={motion.button}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                See Premium Plans
              </Button>
              <Button
                variant="text"
                onClick={handleCloseModal}
                sx={{
                  mt: 1.5,
                  color: '#6b7280',
                  '&:hover': {
                    bgcolor: 'transparent',
                    color: '#4a1e28',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                Close
              </Button>
            </Card>
          </Box>
        )}

        {/* Premium Access Modal */}
        {showPremiumAccessModal && (
          <Box
            sx={{
              position: 'fixed',
              inset: 0,
              bgcolor: 'rgba(0,0,0,0.75)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 50,
            }}
            onClick={() => setShowPremiumAccessModal(false)}
            component={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <Card
              sx={{
                maxWidth: 500,
                width: '90%',
                p: 6,
                borderRadius: 3,
                textAlign: 'center',
                background: 'white',
                boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
              }}
              onClick={(e) => e.stopPropagation()}
              component={motion.div}
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  bgcolor: '#4a1e28',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 3,
                }}
              >
                <LockIcon sx={{ fontSize: 40, color: 'white' }} />
              </Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: '#4a1e28',
                  mb: 2,
                  fontSize: { xs: '1.5rem', md: '2rem' },
                  letterSpacing: '-0.025em',
                }}
              >
                Premium Features Locked
              </Typography>
              <Typography variant="body1" sx={{ color: '#6b7280', mb: 4, lineHeight: 1.6 }}>
                Unlock advanced search filters including Nakshatra, Caste, and Raasi to find your perfect match with deeper astrological compatibility.
              </Typography>
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ color: '#4a1e28', mb: 2, fontWeight: 600 }}>
                  Premium Features Include:
                </Typography>
                <Box sx={{ textAlign: 'left', maxWidth: 300, mx: 'auto' }}>
                  {[
                    '🔒 Nakshatra-based matching',
                    '🔒 Caste compatibility filters',
                    '🔒 Raasi (Zodiac sign) filters',
                    '🔒 Advanced astrological insights',
                    '🔒 Priority profile recommendations'
                  ].map((feature, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        mb: 1,
                        p: 1,
                        bgcolor: '#f8fafc',
                        borderRadius: 1,
                      }}
                    >
                      <Typography variant="body2" sx={{ color: '#374151' }}>
                        {feature}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  onClick={() => {
                    setShowPremiumAccessModal(false);
                    setShowPaywallModal(true);
                  }}
                  sx={{
                    bgcolor: '#4a1e28',
                    color: 'white',
                    fontWeight: 700,
                    py: 1.5,
                    px: 4,
                    borderRadius: 2,
                    '&:hover': {
                      bgcolor: '#3b1720',
                    },
                    transition: 'all 0.3s ease',
                  }}
                  component={motion.button}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Upgrade to Premium
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => setShowPremiumAccessModal(false)}
                  sx={{
                    borderColor: '#4a1e28',
                    color: '#4a1e28',
                    fontWeight: 600,
                    py: 1.5,
                    px: 4,
                    borderRadius: 2,
                    '&:hover': {
                      borderColor: '#3b1720',
                      bgcolor: '#4a1e28',
                      color: 'white',
                    },
                    transition: 'all 0.3s ease',
                  }}
                  component={motion.button}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Maybe Later
                </Button>
              </Box>
            </Card>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default GuestUserDashboard;